import { useEffect, useRef } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import Layout from './components/Layout'
import Comptes from './pages/Comptes'
import Couchages from './pages/Couchages'
import Dashboard from './pages/Dashboard'
import Depenses from './pages/Depenses'
import Familles from './pages/Familles'
import Login from './pages/Login'
import Menus from './pages/Menus'
import Nuitees from './pages/Nuitees'
import PlanningWc from './pages/PlanningWc'
import RepasPage from './pages/Repas'
import { useMe } from './api/hooks'

export default function App() {
  const me = useMe()
  const qc = useQueryClient()
  const location = useLocation()

  // === Revalidation de session sur CHAQUE navigation ========================
  // Dès que l'utilisateur clique sur un onglet (changement de pathname), on
  // invalide la query `me` → /auth/me est rejoué côté serveur. Si la session a
  // été révoquée entre-temps (rotation du mot de passe, purge…), le backend
  // renvoie 401 → useMe passe en isError → on bascule sur l'écran Login.
  //
  // IMPORTANT — on SKIP la toute première exécution : au mount, useMe lance
  // déjà /auth/me ; un invalidate supplémentaire ferait DEUX requêtes en
  // simultané et créerait le flicker « la page tente de se charger plusieurs
  // fois » à l'ouverture d'un nouvel onglet.
  const isFirstMount = useRef(true)
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false
      return
    }
    qc.invalidateQueries({ queryKey: ['me'] })
  }, [location.pathname, qc])

  // 1er chargement (aucune donnée encore) : loader simple.
  if (me.isLoading && !me.data) {
    return <p className="p-6 text-ocean-700">Chargement…</p>
  }

  // Session invalide : on bascule sur Login. Le message poli y apparaîtra
  // si l'utilisateur saisit un mot de passe incorrect.
  if (me.isError || (me.data && !me.data.authenticated)) {
    return <Login />
  }

  // Cas restant : pas encore de données positives (cache vidé) → Login en
  // attendant. App.tsx re-rendra dès que la query résout.
  if (!me.data?.authenticated) {
    return <Login />
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="familles" element={<Familles />} />
        <Route path="nuitees" element={<Nuitees />} />
        <Route path="repas" element={<RepasPage />} />
        <Route path="menus" element={<Menus />} />
        <Route path="couchages" element={<Couchages />} />
        <Route path="depenses" element={<Depenses />} />
        <Route path="comptes" element={<Comptes />} />
        <Route path="planning-wc" element={<PlanningWc />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Route>
    </Routes>
  )
}
